import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import api from "./lib/api";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { StoreProvider } from "./context/StoreContext";
import { SiteContentProvider } from "./context/SiteContentContext";
import SmoothScroll from "./components/SmoothScroll";
import Preloader from "./components/Preloader";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import VerifyBanner from "./components/VerifyBanner";
import AdminEditIndicator from "./components/admin/AdminEditIndicator";

import Home from "./pages/Home";
import Shop from "./pages/Shop";
import Collections from "./pages/Collections";
import ProductDetail from "./pages/ProductDetail";
import Wishlist from "./pages/Wishlist";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import OrderConfirmation from "./pages/OrderConfirmation";
import Account from "./pages/Account";
import Auth from "./pages/Auth";
import About from "./pages/About";
import Journal from "./pages/Journal";
import Contact from "./pages/Contact";
import Verify from "./pages/Verify";

import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminCoupons from "./pages/admin/AdminCoupons";
import AdminCustomers from "./pages/admin/AdminCustomers";
import AdminCategories from "./pages/admin/AdminCategories";
import AdminOccasions from "./pages/admin/AdminOccasions";
import AdminContent from "./pages/admin/AdminContent";

import PosLayout from "./pos/PosLayout";
import PosTerminal from "./pos/PosTerminal";
import PosBills from "./pos/PosBills";
import PosDay from "./pos/PosDay";
import PosCustomers from "./pos/PosCustomers";
import PosCustomerProfile from "./pos/PosCustomerProfile";
import PosStock from "./pos/PosStock";
import PosAlterations from "./pos/PosAlterations";
import PosBarcodes from "./pos/PosBarcodes";
import PosReports from "./pos/PosReports";
import PosSettings from "./pos/PosSettings";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function StorefrontLayout() {
  return (
    <SmoothScroll>
      <div className="min-h-screen flex flex-col">
        <VerifyBanner />
        <Navbar />
        <main className="flex-1">
          <Outlet />
        </main>
        <Footer />
        <AdminEditIndicator />
      </div>
    </SmoothScroll>
  );
}

function MaintenancePage({ status }) {
  const msg = status.message || "We're putting the finishing touches on our new collection. Please check back soon.";
  const tel = (status.phone || "").replace(/[^\d+]/g, "");
  return (
    <div className="min-h-screen bg-[#F6F1EA] flex flex-col items-center justify-center px-6 text-center" data-testid="maintenance-page">
      <img src="/ambrai-logo.png" alt="Amraiya" className="h-24 md:h-32 w-auto mb-10" />
      <p className="text-xs uppercase tracking-[0.3em] text-[#A8845C] mb-4">Store update in progress</p>
      <h1 className="font-serif text-3xl md:text-5xl text-stone-900 mb-6 max-w-2xl">We'll be back shortly</h1>
      <p className="text-stone-600 max-w-md mb-10 text-sm md:text-base leading-relaxed">{msg}</p>
      {status.phone && (
        <a href={`tel:${tel}`} className="text-stone-900 underline underline-offset-4 decoration-[#A8845C]" data-testid="maintenance-phone">
          {status.phone}
        </a>
      )}
      <p className="text-[11px] text-stone-400 mt-16 uppercase tracking-widest">— {status.shop_name} —</p>
    </div>
  );
}

function MaintenanceGate() {
  const [status, setStatus] = useState(null);
  const { user, ready } = useAuth();

  useEffect(() => {
    api.get("/storefront/status").then((r) => setStatus(r.data)).catch(() => setStatus({ offline: false }));
  }, []);

  if (status === null || !ready) return <div className="min-h-screen bg-[#F6F1EA]" />;
  // Logged-in admins can still browse the storefront to preview changes.
  if (status.offline && !(user && user.role === "admin")) return <MaintenancePage status={status} />;
  return <StorefrontLayout />;
}

function App() {
  return (
    <div className="App grain">
      <BrowserRouter>
        <Preloader />
        <ScrollToTop />
        <AuthProvider>
          <StoreProvider>
            <SiteContentProvider>
              <Routes>
              <Route element={<MaintenanceGate />}>
                <Route path="/" element={<Home />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/collections" element={<Collections />} />
                <Route path="/product/:slug" element={<ProductDetail />} />
                <Route path="/wishlist" element={<Wishlist />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/order/:orderId" element={<OrderConfirmation />} />
                <Route path="/account" element={<Account />} />
                <Route path="/about" element={<About />} />
                <Route path="/journal" element={<Journal />} />
                <Route path="/contact" element={<Contact />} />
              </Route>

              <Route path="/auth" element={<Auth />} />
              <Route path="/verify" element={<Verify />} />

              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="categories" element={<AdminCategories />} />
                <Route path="occasions" element={<AdminOccasions />} />
                <Route path="content" element={<AdminContent />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="coupons" element={<AdminCoupons />} />
                <Route path="customers" element={<AdminCustomers />} />
              </Route>

              <Route path="/pos" element={<PosLayout />}>
                <Route index element={<PosTerminal />} />
                <Route path="bills" element={<PosBills />} />
                <Route path="day" element={<PosDay />} />
                <Route path="customers" element={<PosCustomers />} />
                <Route path="customers/:id" element={<PosCustomerProfile />} />
                <Route path="stock" element={<PosStock />} />
                <Route path="alterations" element={<PosAlterations />} />
                <Route path="barcodes" element={<PosBarcodes />} />
                <Route path="reports" element={<PosReports />} />
                <Route path="settings" element={<PosSettings />} />
              </Route>
            </Routes>
            </SiteContentProvider>
          </StoreProvider>
        </AuthProvider>
        <Toaster position="bottom-right" toastOptions={{ style: { fontFamily: "Manrope, sans-serif", borderRadius: 0 } }} />
      </BrowserRouter>
    </div>
  );
}

export default App;
